import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Car, Gauge, Fuel, Calendar, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import VehicleCard from "../components/VehicleCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [vehiclesRes, statsRes] = await Promise.all([
        axios.get(`${API}/vehicles?page_size=6&sort_by=price_asc`),
        axios.get(`${API}/stats`)
      ]);
      setVehicles(vehiclesRes.data.vehicles || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Errore caricamento dati:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] bg-neutral-950 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_auto-feed-importer/artifacts/rsjz27jd_SliderHomeCavw.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16">
          <div className="max-w-2xl">
            <p className="text-blue-500 font-semibold uppercase tracking-widest text-sm mb-4">
              Concessionaria Auto Usate
            </p>
            <h1 
              className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              AUTO USATE<br />
              <span className="text-blue-500">CAVW COMMERCIALE SRL</span>
            </h1>
            <p className="text-zinc-300 text-lg mb-8 max-w-lg">
              Selezione di veicoli usati certificati Volkswagen, Škoda e altri brand premium. 
              Qualità garantita, prezzi trasparenti.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/catalogo">
                <Button 
                  className="bg-blue-600 text-white hover:bg-blue-700 font-bold uppercase tracking-wider px-8 py-6 text-base"
                  data-testid="view-catalog-btn"
                >
                  Sfoglia il Catalogo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {stats && (
        <section className="bg-neutral-950 py-8 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 gap-8 max-w-2xl mx-auto">
              <div className="text-center" data-testid="stat-total">
                <p 
                  className="text-4xl md:text-5xl font-bold text-white mb-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {stats.total_vehicles}
                </p>
                <p className="text-zinc-500 text-sm uppercase tracking-wider">
                  Veicoli Disponibili
                </p>
              </div>
              <div className="text-center" data-testid="stat-brands">
                <p 
                  className="text-4xl md:text-5xl font-bold text-white mb-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {stats.brands_count}
                </p>
                <p className="text-zinc-500 text-sm uppercase tracking-wider">
                  Marche
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Vehicles */}
      <section className="py-16 md:py-24 bg-zinc-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-blue-600 font-semibold uppercase tracking-widest text-sm mb-2">
                In Evidenza
              </p>
              <h2 
                className="text-4xl md:text-5xl font-bold text-zinc-900"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                ULTIME OCCASIONI
              </h2>
            </div>
            <Link 
              to="/catalogo"
              className="hidden md:flex items-center gap-2 text-blue-600 font-semibold uppercase tracking-wide text-sm hover:gap-3 transition-all"
            >
              Vedi Tutti
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white animate-pulse aspect-[4/5]" />
              ))}
            </div>
          ) : vehicles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((vehicle) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white">
              <Car className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
              <h3 
                className="text-2xl font-bold text-zinc-700 mb-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Nessun Veicolo Disponibile
              </h3>
              <p className="text-zinc-500 mb-6">
                Sincronizza i dati dal pannello admin per visualizzare i veicoli.
              </p>
              <Link to="/admin">
                <Button className="bg-blue-600 text-white hover:bg-blue-700">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Vai all'Admin
                </Button>
              </Link>
            </div>
          )}

          <div className="mt-8 text-center md:hidden">
            <Link to="/catalogo">
              <Button className="bg-blue-600 text-white hover:bg-blue-700 w-full max-w-sm">
                Vedi Tutto il Catalogo
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-neutral-950 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            CERCHI UN'AUTO SPECIFICA?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
            Utilizza i nostri filtri avanzati per trovare esattamente quello che stai cercando.
          </p>
          <Link to="/catalogo">
            <Button 
              className="bg-blue-600 text-white hover:bg-blue-700 font-bold uppercase tracking-wider px-10 py-6"
              data-testid="cta-catalog-btn"
            >
              Cerca nel Catalogo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
